import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Reveal } from '@/components/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import { PhotoComparisonSlider } from '@/components/PhotoComparisonSlider';
import {
  Camera, FileText, Video, Globe, Layers, Zap,
  CheckCircle2, ArrowRight, Crown, Home, Clock, ShieldCheck,
  Upload, Sparkles, Check, X, Star, Link2,
  Users, Building2, KeyRound, TrendingUp, UserCheck, Briefcase,
} from 'lucide-react';
import heroBefore from '@/assets/hero-before.jpg';
import heroAfter from '@/assets/hero-after.jpg';
import stagingBefore from '@/assets/staging-before.jpg';
import stagingAfter from '@/assets/staging-after.jpg';
import goldenHourAfter from '@/assets/golden-hour-after.jpg';

/** Shared premium dark-section background + a brighter on-dark accent color. */
const DARK_SECTION =
  'relative overflow-hidden bg-gradient-to-b from-[hsl(222_47%_12%)] via-[hsl(221_46%_9%)] to-[hsl(222_48%_7%)]';
const ACCENT = 'text-[hsl(205_100%_78%)]';

const faqs = [
  {
    q: 'Who is ListBright for?',
    a: 'Real estate agents, brokers, listing and buyer agents, property managers, investors, photographers, and anyone marketing a property online. (Selling your own home? See our Single Property Kit for owners.)',
  },
  {
    q: 'How long does it take to market a listing?',
    a: 'About 10 minutes. Upload your photos — or paste an existing listing URL — and ListBright returns enhanced photos, five description formats, a cinematic video, a microsite, and a print flyer.',
  },
  {
    q: 'Will the descriptions work for MLS and Zillow?',
    a: 'Yes. ListBright generates five formats — Premium (long-form, MLS-ready), Facebook, Instagram, Instagram Stories, and General Marketplace — so you can paste straight into any platform.',
  },
  {
    q: 'Does ListBright handle virtual staging?',
    a: 'Yes. Pick any empty room photo and apply photo-realistic virtual staging in seconds — no $30/photo stager invoice. Furniture style is selectable per photo, and you can re-roll any result.',
  },
  {
    q: 'Can I import a listing I already have online?',
    a: 'Yes. Paste a listing URL from Zillow, Idealista, and other portals — ListBright pulls in every photo and detail automatically, then upgrades the whole listing for you.',
  },
  {
    q: 'Is ListBright affiliated with the National Association of REALTORS® (NAR)?',
    a: 'No. ListBright is an independent software company and is not affiliated with, endorsed by, or sponsored by NAR. REALTOR® is a registered trademark of NAR.',
  },
];

const tools = [
  { icon: Camera, title: 'Listing Photo Enhancement', desc: 'Declutter, golden hour, sky replace, virtual staging — applied automatically to every shot.' },
  { icon: FileText, title: 'MLS & Social Descriptions', desc: 'Five formats per listing: Premium (MLS), Facebook, Instagram, Stories, Marketplace.' },
  { icon: Video, title: 'Cinematic Property Video', desc: 'Real AI motion built from your enhanced photos — ready for reels and YouTube.' },
  { icon: Globe, title: 'Branded Microsite', desc: 'A clean single-property page to text or email buyers, or print as a QR on yard signs.' },
  { icon: Layers, title: 'Print-Ready PDF Flyer', desc: 'A polished one-pager in 28 languages for open houses and door knocking.' },
  { icon: Zap, title: '28-Language Translation', desc: 'Reach international buyers — every description saved in 28 languages, switch in one click.' },
];

const highlights = [
  { icon: Zap, label: 'Listings in ~10 min' },
  { icon: Sparkles, label: 'Virtual staging included' },
  { icon: Layers, label: '6 tools in one' },
  { icon: FileText, label: 'MLS & Zillow ready' },
  { icon: Globe, label: '28 languages' },
];

const steps = [
  {
    num: '1',
    title: 'Add Your Property',
    desc: 'Upload your photos — phone shots are fine, with or without furniture — or just paste an existing listing URL and we import every photo and detail automatically.',
    img: stagingBefore,
    overlay: (
      <div className="absolute inset-0 flex items-center justify-center p-5">
        <div className="w-full max-w-[16rem] rounded-2xl bg-black/45 backdrop-blur-md ring-1 ring-white/20 p-5 text-center space-y-3">
          <Upload className="w-7 h-7 text-white mx-auto" />
          <p className="text-white text-sm font-medium">Upload photos for <span className="font-bold">free</span></p>
          <div className="flex items-center gap-2 text-white/50 text-[11px]">
            <span className="h-px flex-1 bg-white/20" /> or <span className="h-px flex-1 bg-white/20" />
          </div>
          <div className="inline-flex items-center gap-1.5 text-[11px] text-white/90 bg-white/10 rounded-full px-3 py-1.5">
            <Link2 className="w-3.5 h-3.5" /> Paste a listing URL
          </div>
        </div>
      </div>
    ),
  },
  {
    num: '2',
    title: 'AI Builds the Whole Kit',
    desc: 'Every photo is enhanced and staged, five descriptions are written, a video, microsite, and flyer are generated — and it all happens automatically in minutes.',
    img: stagingAfter,
    overlay: (
      <div className="absolute inset-0 flex items-center justify-center p-5">
        <div className="w-full max-w-[17rem] rounded-2xl bg-black/45 backdrop-blur-md ring-1 ring-white/20 p-4 space-y-2.5">
          <div className="flex items-center gap-2 text-white text-xs font-semibold">
            <Sparkles className={`w-4 h-4 ${ACCENT}`} /> Building your kit…
          </div>
          {['Enhancing every photo', 'Writing 5 descriptions', 'Translating to 28 languages', 'Video, microsite & flyer'].map((row) => (
            <div key={row} className="flex items-center gap-2 text-[11px] text-white/85">
              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {row}
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    num: '3',
    title: 'Edit & Publish Anywhere',
    desc: 'Fine-tune anything, switch languages, then paste into MLS and Zillow, post to social, or share your branded microsite — ready to go.',
    img: goldenHourAfter,
    overlay: (
      <>
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-white text-[11px] font-medium">
          <Globe className={`w-3.5 h-3.5 ${ACCENT}`} /> 28 languages
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5">
          {['MLS', 'Zillow', 'Facebook', 'Instagram', 'Microsite'].map((f) => (
            <span key={f} className="text-[10px] px-2 py-1 rounded-full bg-black/50 backdrop-blur text-white/90">{f}</span>
          ))}
        </div>
      </>
    ),
  },
];

const personas = [
  { icon: Home, title: 'Listing Agents', desc: 'Win the listing and sell faster — pro photos and copy in minutes, no decluttering or stager bills.' },
  { icon: UserCheck, title: "Buyer's Agents", desc: 'Help buyers picture the home with on-trend staging and clean, persuasive descriptions.' },
  { icon: KeyRound, title: 'Rental & Leasing', desc: 'Cut vacancy time with compelling visuals and listings that fill units faster.' },
  { icon: Building2, title: 'Brokerages', desc: 'Keep every agent on-brand with consistent, high-quality marketing across all listings.' },
  { icon: Briefcase, title: 'Property Managers', desc: 'Turn units quickly by capturing qualified tenants with attractive, ready-to-post listings.' },
  { icon: TrendingUp, title: 'Investors', desc: 'Market flips and rentals like a pro without hiring a photographer or copywriter.' },
  { icon: Camera, title: 'Photographers', desc: 'Upsell complete marketing packages — staging, video, and copy on top of your shots.' },
  { icon: Users, title: 'Teams', desc: 'Standardize listing marketing across the whole team so every property looks first-class.' },
];

const comparison = {
  columns: ['Do it yourself', 'Hire vendors', 'ListBright'],
  rows: [
    { label: 'Pro-grade listing photos', values: ['Skill required', '$150–300 / shoot', 'Included'] },
    { label: 'Virtual staging', values: [false, '$30 / photo', 'Included'] },
    { label: 'MLS & social copy', values: ['Hours by hand', 'Extra fee', '5 formats, instant'] },
    { label: 'Cinematic property video', values: [false, '$$$ editor', 'Included'] },
    { label: 'Microsite & PDF flyer', values: [false, 'Extra fee', 'Included'] },
    { label: '28-language translation', values: [false, false, 'Included'] },
    { label: 'Turnaround time', values: ['Hours–days', 'Days–weeks', '~10 minutes'] },
    { label: 'Cost', values: ['$50–200 / mo in tools', '$200–500+ / listing', 'One simple plan'] },
  ],
};

export default function RealtorMarketingTools() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'ListBright — Real Estate Marketing Software',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://listbright.ai/real-estate-marketing-software',
      offers: { '@type': 'Offer', category: 'subscription' },
      description: 'All-in-one AI marketing software for real estate agents: photo enhancement, virtual staging, MLS-ready descriptions, video, microsite, and PDF flyers from one upload.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Real Estate Marketing Software — The All-in-One AI Suite for Listings | ListBright"
        description="The all-in-one AI suite for real estate listings: photos, virtual staging, MLS-ready descriptions, video, microsite and flyer — generated from a single upload in about 10 minutes."
        canonical="https://listbright.ai/real-estate-marketing-software"
        breadcrumbs={[{ label: 'Real Estate Marketing Software', path: '/real-estate-marketing-software' }]}
        jsonLd={jsonLd}
      />

      {/* ── Hero (split) ── */}
      <section className="relative overflow-hidden border-b border-border/40 py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] right-1/4 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-8rem] left-1/4 w-80 h-80 bg-accent/40 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-0 px-4 py-1.5 animate-fade-in">
                <Home className="w-3.5 h-3.5 mr-1.5" /> Built for Real Estate Professionals
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] animate-fade-up">
                The All-in-One AI Suite for{' '}
                <span className="text-gradient">Real Estate Listings</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl animate-fade-up" style={{ animationDelay: '80ms' }}>
                Photos, virtual staging, MLS-ready descriptions, cinematic video, microsite and
                flyer — every asset a listing needs, generated from a single upload in about 10
                minutes. Replace your entire marketing stack with one tool.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button asChild size="lg" className="group shadow-lg">
                  <a href="#try">Start Free <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></a>
                </Button>
                <Button asChild size="lg" variant="outline"><Link to="/pricing">See Pricing</Link></Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground pt-1">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> First listing free</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> No credit card</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-primary fill-primary" /> Agents in 30+ countries</span>
              </div>
            </div>

            <Reveal delay={120} className="relative">
              <div className="absolute -inset-5 bg-gradient-to-tr from-primary/20 via-primary/10 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-elevated border border-border/50">
                <PhotoComparisonSlider originalSrc={heroBefore} enhancedSrc={heroAfter} className="aspect-[4/3]" />
              </div>
            </Reveal>
          </div>

          {/* Highlights strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {highlights.map(({ icon: Icon, label }, i) => (
              <Reveal key={label} delay={i * 60} className="flex items-center gap-2.5 justify-center sm:justify-start rounded-xl border border-border/50 bg-card/50 px-4 py-3">
                <Icon className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Live demo (working try-it widget) ── */}
      <section id="try" className="py-16 lg:py-24 border-b border-border/40 bg-muted/30">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <Badge variant="secondary" className="bg-primary/10 text-primary border-0 px-4 py-1.5 mb-4">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Live demo
            </Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">See It on Your Own Listing — Free</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Drag the slider to preview a real enhancement, then drop in your own photo — staged and
              relit in seconds. No signup.
            </p>
          </Reveal>
          <Reveal delay={80}>
            <TryItNowWidget
              inline
              sampleOriginalSrc={stagingBefore}
              sampleEnhancedSrc={stagingAfter}
              onGetStarted={() => navigate('/auth')}
            />
          </Reveal>
        </div>
      </section>

      {/* ── How it works — dark stepped cards ── */}
      <section className={`${DARK_SECTION} py-20 lg:py-28`}>
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(0_0%_100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0_0%_100%) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-0 right-1/4 w-[30rem] h-[30rem] bg-primary/15 rounded-full blur-3xl pointer-events-none" />
        <div className="container max-w-6xl mx-auto px-4 relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="secondary" className={`bg-primary/15 ${ACCENT} border-0 ring-1 ring-primary/25 px-4 py-1.5 mb-4`}>
              <Zap className="w-3.5 h-3.5 mr-1.5" /> How it works
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-3">
              A Complete Listing, Easy as 1, 2, 3
            </h2>
            <p className="text-white/65 text-lg">
              No software to learn, no editing skills required — the AI does the production work.
            </p>
          </Reveal>

          <div className="space-y-6">
            {steps.map((step, i) => (
              <Reveal key={step.num} delay={i * 80}>
                <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-center rounded-3xl ring-1 ring-white/10 bg-white/[0.04] p-6 lg:p-10">
                  <div className={`space-y-5 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                    <div className="w-11 h-11 rounded-full bg-white text-[hsl(222_47%_11%)] flex items-center justify-center font-display font-bold text-lg">
                      {step.num}
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-display font-bold text-white">{step.title}</h3>
                    <p className="text-white/65 leading-relaxed max-w-md">{step.desc}</p>
                  </div>
                  <div className={`relative rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[16/11] ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                    <img src={step.img} alt={step.title} loading="lazy" className="w-full h-full object-cover" />
                    {step.overlay}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tools grid ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              <Layers className="w-4 h-4 text-primary" /> The toolkit
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Realtor Marketing Tools, All in One Place</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Six realtor marketing tools, one upload — no exporting between Canva, ChatGPT, and a virtual stager.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3 group">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Personas — dark grid ── */}
      <section className={`${DARK_SECTION} py-20 lg:py-28`}>
        <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] bg-primary/12 rounded-full blur-3xl pointer-events-none" />
        <div className="container max-w-6xl mx-auto px-4 relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="secondary" className={`bg-primary/15 ${ACCENT} border-0 ring-1 ring-primary/25 px-4 py-1.5 mb-4`}>
              <Users className="w-3.5 h-3.5 mr-1.5" /> Made for Real Estate Professionals
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-3">
              Accelerate Your Success
            </h2>
            <p className="text-white/65 text-lg">
              Whoever you are in the deal, ListBright makes your listings look like a top producer's.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {personas.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 4) * 70}>
                <div className="h-full rounded-2xl ring-1 ring-white/10 bg-white/[0.04] p-6 space-y-3 transition-all duration-300 hover:ring-primary/40 hover:bg-white/[0.07] hover:-translate-y-1">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/40 to-primary/10 ring-1 ring-primary/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-white">{title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison table ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge variant="outline" className="mb-4">The math</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Why Agents Choose ListBright</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Replace a stack of realtor marketing tools and a roster of vendors with one 10-minute workflow.
            </p>
          </Reveal>

          <Reveal className="overflow-x-auto">
            <div className="min-w-[640px] rounded-2xl border border-border/60 overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-4 bg-muted/40">
                <div className="p-4 sm:p-5 text-sm font-semibold text-muted-foreground">What you get</div>
                {comparison.columns.map((col, i) => {
                  const isBrand = i === comparison.columns.length - 1;
                  return (
                    <div
                      key={col}
                      className={`p-4 sm:p-5 text-center text-sm font-bold ${isBrand ? 'bg-primary text-primary-foreground' : 'text-foreground'}`}
                    >
                      {col}
                    </div>
                  );
                })}
              </div>
              {/* Rows */}
              {comparison.rows.map((row, ri) => (
                <div key={row.label} className={`grid grid-cols-4 ${ri % 2 === 1 ? 'bg-muted/20' : 'bg-card'}`}>
                  <div className="p-4 sm:p-5 text-sm font-medium text-foreground flex items-center">{row.label}</div>
                  {row.values.map((val, vi) => {
                    const isBrand = vi === row.values.length - 1;
                    return (
                      <div
                        key={vi}
                        className={`p-4 sm:p-5 text-center text-sm flex items-center justify-center gap-1.5 ${isBrand ? 'bg-primary/5 font-semibold text-foreground' : 'text-muted-foreground'}`}
                      >
                        {val === false ? (
                          <X className="w-4 h-4 text-destructive/60" />
                        ) : isBrand ? (
                          <><Check className="w-4 h-4 text-primary shrink-0" /> {val}</>
                        ) : (
                          val
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="text-center mt-10">
            <Button asChild size="lg" className="group shadow-lg">
              <a href="#try">Try It Free <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></a>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ── Pricing callout (plans for pros) ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-4xl mx-auto px-4">
          <Reveal className="text-center space-y-6">
            <Badge className="mb-2"><Crown className="w-3.5 h-3.5 mr-1.5" /> Plans for every volume</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold">
              Plans Built for How You List
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              List one property a month or fifty — pick the plan that matches your volume and ship
              market-ready listings all month long. No long-term contract, cancel anytime.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-lg">
                <Link to="/pricing">
                  See Plans &amp; Pricing <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#try">Start Free</a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5 pt-2">
              <Clock className="w-4 h-4" /> Every listing market-ready in about 10 minutes.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ (two-column) ── */}
      <section className="py-20 lg:py-28">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16">
            <Reveal className="lg:sticky lg:top-24 self-start space-y-5">
              <Badge variant="outline"><Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" /> FAQ</Badge>
              <h2 className="text-3xl md:text-4xl font-display font-bold leading-tight">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Still need help?{' '}
                <Link to="/contact" className="text-primary font-medium hover:underline">Contact us.</Link>
              </p>
            </Reveal>
            <Reveal delay={100}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="pb-20 lg:pb-28">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center space-y-6 p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
            <h2 className="text-3xl sm:text-4xl font-display font-bold">
              Your next listing, marketed in 10 minutes
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              Upload your photos and see staging, enhancement, and a sample description on your own
              property — free to start.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Button asChild size="lg" className="group text-base px-8 shadow-lg">
                <a href="#try">Start Free <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/pricing">See Pricing</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── NAR disclaimer ── */}
      <section className="py-12 border-t border-border/40">
        <div className="container max-w-3xl mx-auto px-4">
          <p className="text-xs text-muted-foreground text-center leading-relaxed flex items-start justify-center gap-2">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              REALTOR® is a registered trademark of the National Association of REALTORS® (NAR).
              ListBright is an independent platform and is not affiliated with, endorsed by, or
              sponsored by NAR.
            </span>
          </p>
        </div>
      </section>
    </Layout>
  );
}
